import { CountryItem } from 'src/components/country-bar/types';
import { ALL_REGIONS } from './config';

/** A cluster assignment as the grid receives it. */
export interface ClusterChip {
  id: number;
  name: string;
  region_name: string;
}

/** An assignment can only be removed from the scope (tab) it was created in. */
export const isChipDeletable = (chip: ClusterChip, currentRegion: string, regionScoped: boolean): boolean =>
  !regionScoped || chip.region_name === currentRegion;

/**
 * Clusters the bulk "Remove" dialog offers for the selected rows: only the chips that
 * could be removed one by one from this tab (same lock rule), each cluster once.
 * The API removes the tab region's assignments only, so a locked chip would be
 * listed, reported as removed, and stay.
 */
export const removableClusters = (
  rows: { id: string | number; tags?: ClusterChip[] }[],
  selection: readonly (string | number)[],
  currentRegion: string,
  regionScoped: boolean
): ClusterChip[] =>
  Array.from(
    new Map(
      rows
        .filter((row) => selection.includes(row.id))
        .flatMap((row) => row.tags || [])
        .filter((chip) => isChipDeletable(chip, currentRegion, regionScoped))
        .map((chip) => [chip.id, chip] as const)
    ).values()
  );

/** Badge of a locked chip: null for a global one (globe icon), else its country code. */
export const chipRegionCode = (chip: ClusterChip, countries: CountryItem[]): string | null => {
  if (chip.region_name === ALL_REGIONS) return null;
  const country = countries.find((c) => c.country_name === chip.region_name);
  return (country?.country_code ?? chip.region_name).toUpperCase();
};

/** One cluster can be assigned in several regions: its id alone is not a unique key. */
export const chipKey = (chip: ClusterChip): string => `${chip.id}-${chip.region_name}`;
